import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, CaretDown, CaretUp } from '@phosphor-icons/react'
import { selectVariants, SelectVariants } from './selectVariants'
import { cn } from '@/lib/utils'

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

const SelectTrigger = ({
  ref,
  className,
  children,
  size,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Trigger> & SelectVariants) => (
  <SelectPrimitive.Trigger ref={ref} className={cn(selectVariants({ size }), className)} {...props}>
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretDown className='ui-h-4 ui-w-4 ui-opacity-50' />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
)
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectScrollUpButton = ({ ref, className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollUpButton>) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(`ui-flex ui-cursor-default ui-items-center ui-justify-center ui-py-1`, className)}
    {...props}
  >
    <CaretUp className='ui-h-4 ui-w-4' />
  </SelectPrimitive.ScrollUpButton>
)
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = ({ ref, className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.ScrollDownButton>) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(`ui-flex ui-cursor-default ui-items-center ui-justify-center ui-py-1`, className)}
    {...props}
  >
    <CaretDown className='ui-h-4 ui-w-4' />
  </SelectPrimitive.ScrollDownButton>
)
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

const SelectContent = ({ ref, className, children, position = `popper`, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Content>) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        `ui-relative ui-z-50 ui-max-h-56 ui-min-w-[8rem] ui-overflow-hidden ui-rounded-md ui-border ui-bg-popover ui-text-popover-foreground ui-shadow-loose data-[state=open]:ui-animate-in data-[state=closed]:ui-animate-out data-[state=closed]:ui-fade-out-0 data-[state=open]:ui-fade-in-0 data-[state=closed]:ui-zoom-out-95 data-[state=open]:ui-zoom-in-95 data-[side=bottom]:ui-slide-in-from-top-2 data-[side=left]:ui-slide-in-from-right-2 data-[side=right]:ui-slide-in-from-left-2 data-[side=top]:ui-slide-in-from-bottom-2 bg-white`,
        position === `popper` &&
          `data-[side=bottom]:ui-translate-y-1 data-[side=left]:ui--translate-x-1 data-[side=right]:ui-translate-x-1 data-[side=top]:ui--translate-y-1`,
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton ref={ref} />
      <SelectPrimitive.Viewport
        className={cn(
          `ui-p-1`,
          position === `popper` && `ui-h-[var(--radix-select-trigger-height)] ui-w-full ui-min-w-[var(--radix-select-trigger-width)]`
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton ref={ref} />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
)
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = ({ ref, className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label ref={ref} className={cn(`ui-py-1.5 ui-px-8 ui-text-sm ui-font-semibold`, className)} {...props} />
)
SelectLabel.displayName = SelectPrimitive.Label.displayName

const selectItemStyle = `ui-relative ui-flex ui-w-full ui-cursor-default ui-select-none hover:ui-bg-blue-50 ui-items-center ui-rounded-sm ui-py-1.5 ui-px-8 ui-text-sm ui-outline-none focus:ui-bg-blue-50 focus:ui-text-accent-foreground data-[disabled]:ui-pointer-events-none data-[disabled]:ui-opacity-50`
const selectCheckStyle = `ui-absolute ui-left-2 ui-flex ui-h-3.5 ui-w-3.5 ui-items-center ui-justify-center`

const SelectItem = ({
  ref,
  className,
  children,
  variant,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Item> & { variant?: '' | 'small' }) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(selectItemStyle, variant === 'small' ? `ui-min-h-8 ui-py-1 ui-h-8` : `ui-min-h-12`, className)}
    {...props}
  >
    <span className={selectCheckStyle}>
      <SelectPrimitive.ItemIndicator>
        <Check className='ui-h-4 ui-w-4' />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
)

export const SelectItemWithIcon = ({
  ref,
  className,
  children,
  icon,
  iconPosition,
  variant,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Item> & {
  icon: React.ReactNode
  iconPosition: 'left' | 'right'
  variant?: '' | 'small'
}) => (
  <SelectPrimitive.Item ref={ref} className={cn(selectItemStyle, variant === 'small' ? `ui-min-h-8` : `ui-min-h-12`, className)} {...props}>
    <span className={selectCheckStyle}>
      <SelectPrimitive.ItemIndicator>
        <Check className='ui-h-4 ui-w-4' />
      </SelectPrimitive.ItemIndicator>
    </span>
    <div className='ui-flex ui-items-center ui-gap-2 ui-w-full'>
      {iconPosition === `left` && icon}
      <SelectPrimitive.ItemText asChild className='ui-w-full'>
        <div className='ui-w-full'>{children}</div>
      </SelectPrimitive.ItemText>
      {iconPosition === `right` && icon}
    </div>
  </SelectPrimitive.Item>
)

SelectItem.displayName = SelectPrimitive.Item.displayName

const EmptySelectContent = ({
  ref,
  className,
  children,
  position = `popper`,
  ...props
}: React.ComponentPropsWithRef<typeof SelectPrimitive.Content>) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        `ui-relative ui-z-50 ui-p-8 ui-max-h-56 ui-min-w-[8rem] ui-overflow-hidden ui-rounded-md ui-border ui-bg-popover ui-text-popover-foreground ui-shadow-loose data-[state=open]:ui-animate-in data-[state=closed]:ui-animate-out data-[state=closed]:ui-fade-out-0 data-[state=open]:ui-fade-in-0 data-[state=closed]:ui-zoom-out-95 data-[state=open]:ui-zoom-in-95 data-[side=bottom]:ui-slide-in-from-top-2 data-[side=left]:ui-slide-in-from-right-2 data-[side=right]:ui-slide-in-from-left-2 data-[side=top]:ui-slide-in-from-bottom-2 gap-3`,
        position === `popper` &&
          `data-[side=bottom]:ui-translate-y-1 data-[side=left]:ui--translate-x-1 data-[side=right]:ui-translate-x-1 data-[side=top]:ui--translate-y-1`,
        className
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          `ui-p-1`,
          position === `popper` && `ui-h-[var(--radix-select-trigger-height)] ui-w-full ui-min-w-[var(--radix-select-trigger-width)]`
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
)

EmptySelectContent.displayName = `EmptySelectContent`

const SelectSeparator = ({ ref, className, ...props }: React.ComponentPropsWithRef<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator ref={ref} className={cn(`ui-mx-1 ui-my-1 ui-h-px ui-bg-muted`, className)} {...props} />
)
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  EmptySelectContent
}
